import { PageHero } from "@/components/site/Blocks";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import type { Crumb } from "@/lib/schema";

type LegalNoticeSegment = {
  text: string;
  bold?: boolean;
  href?: string;
};

type LegalNoticeBlock =
  | { type: "lines"; lines: string[] }
  | { type: "paragraph"; segments: LegalNoticeSegment[] };

type LegalNoticeSection = {
  title: string;
  blocks: LegalNoticeBlock[];
};

export type LegalNoticeContent = {
  lang: "en" | "tr";
  title: string;
  subtitle: string;
  labels: { toc: string; download: string };
  sections: LegalNoticeSection[];
};

/** Loads the legal notice verbatim from the public legal-documents collection. */
export async function loadLegalNotice(lang: "en" | "tr"): Promise<LegalNoticeContent> {
  const { data, error } = await supabase
    .from("legal_documents")
    .select("content")
    .eq("doc_key", "impressum")
    .eq("lang", lang)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new Error("Legal notice not found");
  return data.content as unknown as LegalNoticeContent;
}

const sectionId = (index: number) => `section-${index + 1}`;

function Segment({ segment }: { segment: LegalNoticeSegment }) {
  const value = segment.bold ? <strong className="text-foreground">{segment.text}</strong> : segment.text;
  if (!segment.href) return value;

  const external = segment.href.startsWith("http");
  return (
    <a
      href={segment.href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="break-words text-foreground underline underline-offset-4 hover:text-primary"
    >
      {value}
    </a>
  );
}

export function LegalNoticeDocument({
  content,
  trail,
  eyebrow,
  displayTitle,
}: {
  content: LegalNoticeContent;
  trail: readonly Crumb[];
  eyebrow: string;
  /** Hero/print heading override; the document copy itself stays in the database. */
  displayTitle?: string;
}) {
  const heading = displayTitle ?? content.title;
  return (
    <>
      <div className="print:hidden">
        <PageHero trail={trail} eyebrow={eyebrow} title={heading} intro={content.subtitle} />
      </div>

      <div className="section-shell py-12 print:py-0">
        <article className="mx-auto max-w-3xl">
          <h1 className="hidden font-display text-2xl font-extrabold print:block">{heading}</h1>

          <div className="flex justify-end print:hidden">
            <Button type="button" variant="outline" onClick={() => window.print()}>
              {content.labels.download}
            </Button>
          </div>

          <nav aria-labelledby="legal-notice-toc" className="mt-8 rounded-lg border border-border p-6 print:hidden">
            <h2 id="legal-notice-toc" className="font-display text-lg font-extrabold text-foreground">
              {content.labels.toc}
            </h2>
            <ol className="mt-4 space-y-2 text-sm">
              {content.sections.map((section, index) => (
                <li key={section.title}>
                  <a href={`#${sectionId(index)}`} className="text-muted-foreground hover:text-primary">
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="mt-12 space-y-12">
            {content.sections.map((section, index) => (
              <section key={section.title} id={sectionId(index)} className="scroll-mt-28 break-inside-avoid-page">
                <h2 className="font-display text-xl font-bold text-foreground">{section.title}</h2>
                <div className="mt-4 space-y-4 text-muted-foreground print:text-foreground">
                  {section.blocks.map((block, blockIndex) => {
                    if (block.type === "lines") {
                      return (
                        <p key={blockIndex} className="whitespace-pre-line">
                          {block.lines.map((line, lineIndex) => (
                            <span key={`${line}-${lineIndex}`}>
                              {lineIndex === 0 ? <strong className="text-foreground">{line}</strong> : line}
                              {lineIndex < block.lines.length - 1 && <br />}
                            </span>
                          ))}
                        </p>
                      );
                    }

                    return (
                      <p key={blockIndex}>
                        {block.segments.map((segment, segmentIndex) => (
                          <Segment key={`${segment.text}-${segmentIndex}`} segment={segment} />
                        ))}
                      </p>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </article>
      </div>
    </>
  );
}

export function LegalNoticePending() {
  return (
    <div className="section-shell py-32 text-center text-muted-foreground" role="status">
      …
    </div>
  );
}

export function LegalNoticeError({ message }: { message: string }) {
  return (
    <div className="section-shell py-32 text-center" role="alert">
      <p className="text-foreground">{message}</p>
    </div>
  );
}